import React, { useState, useEffect } from "react";
import { useApi } from "../../../hooks/use-api";
import { useQuestionBankHandler } from "../handler";
import { LatexRenderer, CustomTypeDropdown, renderHighlightedString } from "../imported-questions-modal";
import { normalizeLatexString } from "../../../components/latex-renderer";
import "./style.sass";

const sanitizeImageUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  let trimmed = url.trim();
  trimmed = trimmed.replace(/(%22|%27|["',\\\/])+$/gi, "").trim();
  trimmed = trimmed.replace(/^(%22|%27|["'\\\/])+/gi, "").trim();
  if (
    !trimmed ||
    trimmed === "null" ||
    trimmed === "undefined" ||
    trimmed === "none" ||
    trimmed === "[]" ||
    trimmed === "{}"
  ) {
    return null;
  }
  if (trimmed.includes("question_assets")) {
    const assetPart = trimmed.substring(trimmed.indexOf("question_assets"));
    return `https://rovianai.com/secure-api/${assetPart}`;
  }
  if (trimmed.includes("visal1chatbotvm.southindia.cloudapp.azure.com")) {
    trimmed = trimmed.replace("https://", "http://");
    if (!trimmed.includes(":8001")) {
      trimmed = trimmed.replace(
        "visal1chatbotvm.southindia.cloudapp.azure.com",
        "visal1chatbotvm.southindia.cloudapp.azure.com:8001"
      );
    }
    trimmed = trimmed.replace(":8001/secure-api/", ":8001/");
  }
  return trimmed;
};

const isValidImageUrl = (url) => {
  const sanitized = sanitizeImageUrl(url);
  if (!sanitized) return false;
  return (
    sanitized.startsWith("http://") ||
    sanitized.startsWith("https://") ||
    sanitized.startsWith("data:image/") ||
    sanitized.startsWith("/")
  );
};

const getValidOptImg = (img) => {
  const sanitized = sanitizeImageUrl(img);
  return isValidImageUrl(sanitized) ? sanitized : null;
};

const handleImageError = (e) => {
  let currentSrc = e.target.src || "";
  if (!e.target.dataset.retryAttempted) {
    e.target.dataset.retryAttempted = "true";
    currentSrc = currentSrc.replace(/(%22|%27|["',\\\/])+$/gi, "");
    if (currentSrc.includes("visal1chatbotvm.southindia.cloudapp.azure.com")) {
      let newSrc = currentSrc;
      if (newSrc.startsWith("https://")) {
        newSrc = newSrc.replace("https://", "http://");
      }
      if (!newSrc.includes(":8001")) {
        newSrc = newSrc.replace(
          "visal1chatbotvm.southindia.cloudapp.azure.com",
          "visal1chatbotvm.southindia.cloudapp.azure.com:8001"
        );
      }
      if (newSrc !== currentSrc) {
        e.target.src = newSrc;
        return;
      }
    }
  }
  e.target.style.display = "none";
};

const parseIsUsed = (isUsed, usedCount) => {
  if (isUsed !== undefined && isUsed !== null) {
    if (typeof isUsed === "boolean") return isUsed;
    if (typeof isUsed === "string") {
      const s = isUsed.trim().toLowerCase();
      if (s === "false" || s === "0" || s === "no") return false;
      if (s === "true" || s === "1" || s === "yes") return true;
    }
    if (typeof isUsed === "number") return isUsed > 0;
    return Boolean(isUsed);
  }
  if (usedCount !== undefined && usedCount !== null) {
    return Number(usedCount) > 0;
  }
  return undefined;
};

const cleanOptionText = (text) => {
  if (!text) return "";
  const s = String(text).trim();
  if (/^\s*\(?\s*\[?\s*image\s*([_-]?\s*option)?\s*\]?\s*\)?\s*$/i.test(s)) {
    return "";
  }
  return s;
};

export const TestDetailsModal = ({ test, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("All Question Types");
  const [filterValidation, setFilterValidation] = useState("all");
  const [onlyNotUsed, setOnlyNotUsed] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [pollCount, setPollCount] = useState(0);
  const [jobStatus, setJobStatus] = useState("PARSED");

  // Image Zoom Lightbox Modal State
  const [zoomModalImage, setZoomModalImage] = useState(null);
  const [zoomScale, setZoomScale] = useState(1.5);

  const handleOpenZoom = (e, fallbackSrc, title) => {
    if (e && e.stopPropagation) e.stopPropagation();
    let finalSrc = fallbackSrc;
    const parentBox = e?.currentTarget?.closest(".img_preview_box");
    const activeImg = parentBox?.querySelector("img");
    if (activeImg && (activeImg.currentSrc || activeImg.src)) {
      finalSrc = activeImg.currentSrc || activeImg.src;
    }
    setZoomScale(1.5);
    setZoomModalImage({ src: finalSrc, title });
  };

  const api = useApi();
  const { fetchQuestionPaperQuestions } = useQuestionBankHandler(api);

  const rawTitle = typeof test === "string"
    ? test.trim()
    : (test?.testName || test?.test_name || test?.paper_name || test?.test_title || test?.name || test?.title || "").trim();
  const testTitle = rawTitle || "Assessment Test";

  useEffect(() => {
    let isMounted = true;
    let pollTimer = null;

    const loadQuestions = async () => {
      if (!rawTitle) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await fetchQuestionPaperQuestions({ testName: rawTitle, page });

        if (isMounted) {
          const currentStatus = String(res?.job_status || "PARSED").toUpperCase();
          setJobStatus(currentStatus);
          const isStillProcessing = ["PARSING", "PENDING", "PROCESSING", "IN_PROGRESS"].includes(currentStatus);

          const rawQuestions =
            res?.questions ||
            res?.data ||
            res?.items ||
            res?.question_list ||
            res?.question_paper?.questions ||
            (Array.isArray(res) ? res : []);

          if (rawQuestions && rawQuestions.length > 0) {
            const formatted = rawQuestions.map((q, idx) => {
              const hasOptText =
                Boolean(q.options?.A || q.options?.a || q.optionA || q.option_a) ||
                Boolean(q.options?.B || q.options?.b || q.optionB || q.option_b) ||
                Boolean(q.options?.C || q.options?.c || q.optionC || q.option_c) ||
                Boolean(q.options?.D || q.options?.d || q.optionD || q.option_d) ||
                Boolean(q.options?.E || q.options?.e || q.optionE || q.option_e);

              const hasOptImg =
                Boolean(getValidOptImg(q.option_images?.a || q.option_images?.A)) ||
                Boolean(getValidOptImg(q.option_images?.b || q.option_images?.B)) ||
                Boolean(getValidOptImg(q.option_images?.c || q.option_images?.C)) ||
                Boolean(getValidOptImg(q.option_images?.d || q.option_images?.D)) ||
                Boolean(getValidOptImg(q.option_images?.e || q.option_images?.E));

              const rawTypeStr = String(q.question_type || q.type || "").trim().toUpperCase();
              const hasOptions = hasOptText || hasOptImg;
              const isMcq = rawTypeStr === "MCQ" && hasOptions;
              const finalType = isMcq ? "MCQ" : "Numerical";

              const rawImageUrls = Array.isArray(q.image_urls)
                ? q.image_urls
                : q.image_url
                ? [q.image_url]
                : q.image
                ? [q.image]
                : [];

              const imageUrls = rawImageUrls
                .map(sanitizeImageUrl)
                .filter(Boolean)
                .filter(isValidImageUrl);

              const optionImages = q.option_images
                ? {
                    A: getValidOptImg(q.option_images.a || q.option_images.A),
                    B: getValidOptImg(q.option_images.b || q.option_images.B),
                    C: getValidOptImg(q.option_images.c || q.option_images.C),
                    D: getValidOptImg(q.option_images.d || q.option_images.D),
                    E: getValidOptImg(q.option_images.e || q.option_images.E),
                  }
                : q.optionImages || null;

              const rawCorrectAns = (q.correct_answer !== undefined && q.correct_answer !== null)
                ? String(q.correct_answer).trim()
                : (q.answer !== undefined && q.answer !== null ? String(q.answer).trim() : "");

              const rawError = q.validation_errors || q.validationError || q.error_message || q.error || q.reasons;
              const hasRawError = Boolean(
                rawError &&
                  (typeof rawError === "string"
                    ? rawError.trim()
                    : Array.isArray(rawError)
                    ? rawError.length > 0
                    : true)
              );
              const apiIsValid = q.is_valid !== undefined && q.is_valid !== null ? Boolean(q.is_valid) : true;

              const isValid = apiIsValid && !hasRawError;
              const validationError = !isValid
                ? typeof rawError === "string" && rawError.trim()
                  ? rawError.trim()
                  : Array.isArray(rawError) && rawError.length > 0
                  ? rawError.join(". ")
                  : "This question could not be extracted correctly and need manual verification"
                : null;

              return {
                id: q.id || q._id || `Q_${idx + 1}`,
                type: finalType,
                text: normalizeLatexString(q.question_text || q.text || ""),
                correct_answer: rawCorrectAns || (isMcq ? "A" : ""),
                solution: normalizeLatexString(q.solution || q.explanation || q.answer || (!isMcq ? (rawCorrectAns || "") : "")),
                optionA: normalizeLatexString(cleanOptionText(q.options?.A || q.options?.a || q.optionA || q.option_a || "")),
                optionB: normalizeLatexString(cleanOptionText(q.options?.B || q.options?.b || q.optionB || q.option_b || "")),
                optionC: normalizeLatexString(cleanOptionText(q.options?.C || q.options?.c || q.optionC || q.option_c || "")),
                optionD: normalizeLatexString(cleanOptionText(q.options?.D || q.options?.d || q.optionD || q.option_d || "")),
                optionE: normalizeLatexString(cleanOptionText(q.options?.E || q.options?.e || q.optionE || q.option_e || "")),
                imageUrls,
                optionImages,
                isValid,
                validationError,
                is_used: parseIsUsed(q.is_used, q.used_count),
                used_count: q.used_count !== undefined && q.used_count !== null ? Number(q.used_count) : undefined,
              };
            });
            setQuestions(formatted);
            const total = res?.total_count || res?.total || rawQuestions.length;
            const detectedPageSize = res?.page_size || res?.limit || (page === 1 ? formatted.length : 10) || 10;
            const pagesCount = res?.total_pages || (total > 0 ? Math.ceil(total / detectedPageSize) : 1);
            setTotalPages(pagesCount);
          } else {
            setQuestions([]);
            setTotalPages(1);
          }

          if (isStillProcessing) {
            pollTimer = setTimeout(() => {
              if (isMounted) {
                setPollCount((prev) => prev + 1);
              }
            }, 2500);
          }
        }
      } catch (err) {
        console.error("Error loading test details questions", err);
        if (isMounted) {
          setQuestions([]);
          setTotalPages(1);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadQuestions();
    return () => {
      isMounted = false;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [testTitle, page, pollCount]);

  const getNormalizedCorrectAnswer = (ans) => {
    if (!ans) return "A";
    const str = String(ans).trim().toUpperCase();
    if (str === "A" || str === "OPTION A" || str === "OPTIONA") return "A";
    if (str === "B" || str === "OPTION B" || str === "OPTIONB") return "B";
    if (str === "C" || str === "OPTION C" || str === "OPTIONC") return "C";
    if (str === "D" || str === "OPTION D" || str === "OPTIOND") return "D";
    if (str === "E" || str === "OPTION E" || str === "OPTIONE") return "E";
    if (str.startsWith("OPTION ")) return str.replace("OPTION ", "").trim();
    return "A";
  };

  const filteredQuestions = questions.filter((q) => {
    const matchesSearch = q.text ? q.text.toLowerCase().includes(searchTerm.toLowerCase()) : true;
    const matchesType =
      filterType === "All Question Types" || q.type.toLowerCase() === filterType.toLowerCase();

    return matchesSearch && matchesType;
  });

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (page > 3) {
        pages.push("...");
      }
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (page < totalPages - 2) {
        pages.push("...");
      }
      pages.push(totalPages);
    }

    return (
      <div className="pagination">
        <button
          type="button"
          className="page_nav"
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
        >
          {`<`}
        </button>

        {pages.map((p, idx) => {
          if (p === "...") {
            return (
              <span key={`dots-${idx}`} className="dots">
                ...
              </span>
            );
          }
          return (
            <button
              key={p}
              type="button"
              className={`page_num ${page === p ? "active" : ""}`}
              onClick={() => setPage(p)}
            >
              {p}
            </button>
          );
        })}

        <button
          type="button"
          className="page_nav"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
        >
          {`>`}
        </button>
      </div>
    );
  };

  return (
    <div className="test_details_modal_overlay" onClick={onClose}>
      <div className="test_details_modal_box" onClick={(e) => e.stopPropagation()}>
        {/* Top Header */}
        <div className="tdm_header_container">
          <h2>{testTitle}</h2>
          <svg
            className="tdm_close_icon"
            onClick={onClose}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#4B5563"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>

        {/* Filter Controls Bar */}
        <div className="filter_bar">
          <div className="search_box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search Questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <CustomTypeDropdown
            value={filterType}
            onChange={(val) => setFilterType(val)}
            options={[
              { value: "All Question Types", label: "All Question Types" },
              { value: "MCQ", label: "MCQ" },
              { value: "Numerical", label: "Numerical" },
            ]}
          />
        </div>

        {/* Questions Cards List */}
        <div className="questions_list">
          {["PARSING", "PENDING", "PROCESSING", "IN_PROGRESS"].includes(String(jobStatus).toUpperCase()) ? (
            <div style={{ textAlign: "center", padding: "50px 20px", color: "#6B7280" }}>
              <div className="iqm_round_spinner" style={{ margin: "0 auto 16px" }}></div>
              <h4 style={{ fontSize: "16px", fontWeight: 700, color: "#111827", marginBottom: "6px" }}>Parsing questions in progress...</h4>
              <p style={{ fontSize: "13.5px", color: "#6B7280" }}>
                AI parsing is running in background. Questions will load automatically once job status is PARSED...
              </p>
            </div>
          ) : loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6B7280" }}>
              Loading test questions...
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6B7280" }}>
              No questions found for this test.
            </div>
          ) : (
            filteredQuestions.map((q) => {
              const isMcq = (q.type || "").toLowerCase() === "mcq" || Boolean(q.optionA || q.optionB || q.optionC || q.optionD || q.optionE || (q.optionImages && (q.optionImages.A || q.optionImages.B || q.optionImages.C || q.optionImages.D || q.optionImages.E)));
              const normCorrect = getNormalizedCorrectAnswer(q.correct_answer);

              return (
                <div key={q.id} className={`question_card ${!q.isValid ? "has_error" : ""}`}>
                  <div className="card_top">
                    <div className="card_top_left">
                      {!q.isValid ? (
                        <div className="error_banner_row">
                          <span className="error_tag">Error</span>
                          <span className="error_msg">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                              <line x1="12" y1="9" x2="12" y2="13" />
                              <line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                            {q.validationError || "This question could not be extracted correctly and need manual review"}
                          </span>
                        </div>
                      ) : (
                        <span className={`type_tag ${q.type.toLowerCase()}`}>
                          {q.type}
                        </span>
                      )}

                      {q.is_used === false || (q.is_used === undefined && q.used_count !== undefined && Number(q.used_count) === 0) ? (
                        <span className="usage_badge not_used">
                          Not used
                        </span>
                      ) : Boolean((q.is_used || Number(q.used_count) > 0) && q.used_count !== undefined && Number(q.used_count) > 0) ? (
                        <span className="used_count_badge">
                          Used {q.used_count} {Number(q.used_count) === 1 ? "time" : "times"}
                        </span>
                      ) : q.is_used === true ? (
                        <span className="usage_badge used">
                          Used
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="question_main">
                    <span className="q_num">{renderHighlightedString(q.id, searchTerm)}</span>
                    <div className="q_text_block">
                      <p className="q_text"><LatexRenderer text={q.text} highlight={searchTerm} /></p>
                    </div>
                  </div>

                  {q.imageUrls && q.imageUrls.length > 0 && (
                    <div className="q_image_container">
                      {q.imageUrls.map((imgUrl, i) => (
                        <div key={i} className="img_preview_box">
                          <img
                            key={i}
                            src={imgUrl}
                            alt={`Question ${q.id} Diagram ${i + 1}`}
                            className="q_diagram_img"
                            onError={handleImageError}
                            onClick={(e) => handleOpenZoom(e, imgUrl, `Question ${q.id} Diagram ${i + 1}`)}
                            title="Click to zoom in"
                          />
                          <button
                            type="button"
                            className="img_zoom_action_btn"
                            title="Click to Zoom In"
                            onClick={(e) => handleOpenZoom(e, imgUrl, `Question ${q.id} Diagram ${i + 1}`)}
                          >
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                            >
                              <circle cx="11" cy="11" r="8"></circle>
                              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                              <line x1="11" y1="8" x2="11" y2="14"></line>
                              <line x1="8" y1="11" x2="14" y2="11"></line>
                            </svg>
                            <span>Zoom In</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {isMcq ? (
                    <div className="options_grid">
                      {[
                        { key: "A", text: q.optionA, img: q.optionImages?.A },
                        { key: "B", text: q.optionB, img: q.optionImages?.B },
                        { key: "C", text: q.optionC, img: q.optionImages?.C },
                        { key: "D", text: q.optionD, img: q.optionImages?.D },
                        ...(q.optionE || q.optionImages?.E ? [{ key: "E", text: q.optionE, img: q.optionImages?.E }] : []),
                      ].map((opt) => {
                        const isCorrect = normCorrect === opt.key;
                        return (
                          <div
                            key={opt.key}
                            className={`option_item ${isCorrect ? "correct_option" : ""}`}
                          >
                            <span className="opt_badge">{opt.key}</span>
                            <div className="opt_content">
                              {cleanOptionText(opt.text) !== "" && (
                                <span className="opt_text"><LatexRenderer text={cleanOptionText(opt.text)} inline highlight={searchTerm} /></span>
                              )}
                              {opt.img && (
                                <div className="opt_img_wrapper">
                                  <div className="img_preview_box opt_box">
                                    <img
                                      src={opt.img}
                                      alt={`Option ${opt.key}`}
                                      className="opt_diagram_img"
                                      onError={handleImageError}
                                      onClick={(e) => handleOpenZoom(e, opt.img, `Question ${q.id} - Option ${opt.key}`)}
                                      title="Click to zoom in"
                                    />
                                    <button
                                      type="button"
                                      className="img_zoom_action_btn opt_zoom_btn"
                                      title="Click to Zoom In"
                                      onClick={(e) => handleOpenZoom(e, opt.img, `Question ${q.id} - Option ${opt.key}`)}
                                    >
                                      <svg
                                        width="10"
                                        height="10"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                      >
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                        <line x1="11" y1="8" x2="11" y2="14"></line>
                                        <line x1="8" y1="11" x2="14" y2="11"></line>
                                      </svg>
                                      <span>Zoom In</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            {isCorrect && (
                              <span className="correct_tag">
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                  <polyline points="20 6 9 17 4 12" />
                                </svg>
                                Correct
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="short_answer_block">
                      <span className="short_ans_label">Correct Answer:</span>
                      <div className="short_ans_box">
                        <LatexRenderer text={q.correct_answer || q.solution || "No answer provided."} highlight={searchTerm} />
                      </div>
                      <span className="char_count_label">
                        {(q.correct_answer || q.solution || "").length} / 200 characters
                      </span>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="modal_footer">
          <div></div>
          {renderPagination()}
          <div></div>
        </div>

        {/* ── Image Zoom Lightbox Modal ── */}
        {zoomModalImage && (
          <div
            className="qb_image_zoom_overlay"
            onClick={() => {
              setZoomModalImage(null);
              setZoomScale(1.5);
            }}
          >
            <div
              className="qb_image_zoom_modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="zoom_modal_header">
                <span className="zoom_modal_title">
                  {zoomModalImage.title || "Image Preview"}
                </span>
                <div className="zoom_controls_group">
                  <button
                    type="button"
                    className="zoom_ctrl_btn"
                    title="Zoom Out"
                    onClick={() => setZoomScale((prev) => Math.max(0.5, Number((prev - 0.25).toFixed(2))))}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                  </button>
                  <span className="zoom_scale_indicator">
                    {Math.round(zoomScale * 100)}%
                  </span>
                  <button
                    type="button"
                    className="zoom_ctrl_btn"
                    title="Zoom In"
                    onClick={() => setZoomScale((prev) => Math.min(3.5, Number((prev + 0.25).toFixed(2))))}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                    >
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      <line x1="11" y1="8" x2="11" y2="14"></line>
                      <line x1="8" y1="11" x2="14" y2="11"></line>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="zoom_ctrl_btn reset_btn"
                    title="Reset Zoom"
                    onClick={() => setZoomScale(1.5)}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="zoom_close_btn"
                    title="Close (Esc)"
                    onClick={() => {
                      setZoomModalImage(null);
                      setZoomScale(1.5);
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="zoom_modal_body">
                <div className="zoom_img_scroll_container">
                  <img
                    key={zoomModalImage.src}
                    src={zoomModalImage.src}
                    alt={zoomModalImage.title || "Zoomed diagram"}
                    className="zoom_preview_img"
                    style={{
                      transform: `scale(${zoomScale})`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
